import { type KeyboardEvent as ReactKeyboardEvent, type MouseEvent as ReactMouseEvent, type TouchEvent as ReactTouchEvent, useMemo, useState } from 'react'
import type { CommonStatsAppType } from '@api/dashboard/state.types'
import { IWidgetModskillAppChartUI } from './types'
import { useHorizontalDragScroll } from '@hooks/useHorizontalDragScroll'
import { usePulseHighlight } from '@hooks/usePulseHighlight'

import { Button, Container, Divider, Empty, Flex, Grid, Loader, Toogle } from '@ui/index'

import imgEmty from '@img/empty.svg'
import css from './ui.module.scss'
import { IconApplications, IconSettings } from '@img/icons'
import { Popover } from 'antd'

type TChartViewMode = 'classic' | 'percent'
type TWeekdayKey = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun'
type TApplicationPulseKey = 'total' | 'delayCall' | 'contracts' | 'otherStatuses'
type TApplicationStatsItem = {
    total: number
    new: number
    delayCall: number
    contracts: number
    cancel: number
    notClient: number
    double: number
    ignore: number
}
type TDayChartItem = TApplicationStatsItem & {
    year: number
    month: number
    day: number
}
type TChartStatRowItem = TApplicationStatsItem & Partial<TDayChartItem>

const defaultWeekdays: Record<TWeekdayKey, boolean> = {
    mon: true,
    tue: true,
    wed: true,
    thu: true,
    fri: true,
    sat: true,
    sun: true
}

/** UI виджета - Статистика заявок YaSkill Chart */
export const WidgetYaskillAppChartUI = ({
    txtCurrentYear, txtCurrentMonth,
    showStatisticsData,

    totalsYears, totalsDays,

    typeStats, yearDetailStatsApp, monthDetailStatsApp,
    itemsYear, itemsMonth, itemsDays,

    getMonthName
}: IWidgetModskillAppChartUI) => {
    const [chartViewMode, setChartViewMode] = useState<TChartViewMode>('classic')
    const [weekdays, setWeekdays] = useState<Record<TWeekdayKey, boolean>>(defaultWeekdays)
    const dragScrollProps = useHorizontalDragScroll()
    const { getPulseClassName, triggerPulse } = usePulseHighlight()
    const CHART_MIN_VALUE_HEIGHT = 15
    const CHART_COMPACT_VALUE_HEIGHT = CHART_MIN_VALUE_HEIGHT - 3
    const CHART_COMPACT_VALUE_THRESHOLD = 20
    const CHART_AGGREGATED_MAX_HEIGHT = 260
    const CHART_AGGREGATED_MIN_VISIBLE_HEIGHT = 12
    const CHART_DEFAULT_CONTENT_MIN_HEIGHT = 300
    const CHART_REDUCED_CONTENT_MIN_HEIGHT = 140
    const CHART_CONTENT_VERTICAL_OFFSET = 48

    const getPercentValue = (value?: number, total?: number) => {
        if (!total || Number(total) <= 0) return 0
        return Math.round((Number(value) / Number(total)) * 1000) / 10
    }

    const getConversionPercent = (contracts?: number, total?: number) => {
        if (!total || Number(total) <= 0) return 0
        return Math.round((Number(contracts) / Number(total)) * 1000) / 10
    }

    const getAvg = (total?: number, count?: number) => {
        if (!count || Number(count) <= 0) return 0
        return (Math.round((Number(total) / Number(count)) * 10) / 10) || 0
    }

    const getScaledHeight = (
        value: number,
        minValue: number,
        unitHeight = CHART_MIN_VALUE_HEIGHT,
        minVisibleHeight = 0
    ) => {
        if (value <= 0 || minValue <= 0) return 0

        const height = (value / minValue) * unitHeight

        return Number(Math.max(minVisibleHeight, height).toFixed(2))
    }

    const getStackedSegmentHeights = (
        segmentValues: number[],
        minValue: number,
        unitHeight: number,
        minVisibleHeight = 0
    ) => {
        return segmentValues.map((value) => getScaledHeight(value, minValue, unitHeight, minVisibleHeight))
    }

    const getStackedTotalHeight = (
        totalValue: number,
        minValue: number,
        unitHeight: number,
        segmentValues: number[] = [],
        minVisibleHeight = 0
    ) => {
        if (totalValue <= 0 || minValue <= 0) return 0

        const totalHeight = getScaledHeight(totalValue, minValue, unitHeight, minVisibleHeight)
        const stackedSegmentsHeight = getStackedSegmentHeights(segmentValues, minValue, unitHeight, minVisibleHeight)
            .reduce((sum, height) => sum + height, 0)

        return Math.max(totalHeight, Math.ceil(stackedSegmentsHeight))
    }

    const getOtherStatusesValue = (item: {
        new: number
        cancel: number
        notClient: number
        ignore: number
    }) => item.new + item.cancel + item.notClient + item.ignore

    const getDisplayOtherStatuses = (otherStatuses: number, total: number) => {
        return chartViewMode === 'percent'
            ? getPercentValue(otherStatuses, total)
            : otherStatuses
    }

    const getDisplayTotal = (total: number) => {
        if (chartViewMode === 'percent') {
            return total > 0 ? 100 : 0
        }

        return total
    }

    const getDisplayDelayCall = (delayCall: number, total: number) => {
        return chartViewMode === 'percent'
            ? getPercentValue(delayCall, total)
            : delayCall
    }

    const getDisplayContracts = (contracts: number, total: number) => {
        return chartViewMode === 'percent'
            ? getPercentValue(contracts, total)
            : contracts
    }

    const getChartScaleValues = (item: {
        total: number
        delayCall: number
        contracts: number
        new: number
        cancel: number
        notClient: number
        ignore: number
    }) => [
        getDisplayTotal(item.total),
        getDisplayContracts(item.contracts, item.total),
        getDisplayDelayCall(item.delayCall, item.total),
        getDisplayOtherStatuses(getOtherStatusesValue(item), item.total)
    ]

    const getMinPositiveValue = (values: number[]) => {
        const positiveValues = values.filter((value) => value > 0)

        return positiveValues.length > 0 ? Math.min(...positiveValues) : 1
    }

    const getMaxPositiveValue = (values: number[]) => {
        const positiveValues = values.filter((value) => value > 0)

        return positiveValues.length > 0 ? Math.max(...positiveValues) : 1
    }

    const getScaleUnitHeight = (minValue: number, maxValue: number, maxHeight?: number, baseUnitHeight = CHART_MIN_VALUE_HEIGHT) => {
        if (!maxHeight || minValue <= 0 || maxValue <= 0) return baseUnitHeight

        return Math.min(baseUnitHeight, maxHeight / (maxValue / minValue))
    }

    const hasCompactChartBars = (items: TApplicationStatsItem[]) => items.some((item) => item.total > CHART_COMPACT_VALUE_THRESHOLD)

    const getAdaptiveChartContentMinHeight = (columnHeights: number[]) => {
        const maxColumnHeight = Math.max(0, ...columnHeights)

        if (maxColumnHeight <= 0) return CHART_DEFAULT_CONTENT_MIN_HEIGHT

        return Math.min(
            CHART_DEFAULT_CONTENT_MIN_HEIGHT,
            Math.max(CHART_REDUCED_CONTENT_MIN_HEIGHT, Math.ceil(maxColumnHeight + CHART_CONTENT_VERTICAL_OFFSET))
        )
    }

    const getPulseClass = (pulseKey: TApplicationPulseKey) => {
        const pulseClassKey = getPulseClassName(pulseKey)

        return pulseClassKey ? css[pulseClassKey] : ''
    }

    const getPulseTargetClassName = (pulseKey: TApplicationPulseKey, ...classNames: string[]) => {
        return [css['pulse_clickable'], getPulseClass(pulseKey), ...classNames].filter(Boolean).join(' ')
    }

    const handlePulseMouseDown = (pulseKey: TApplicationPulseKey, stopPropagation = false) => (event: ReactMouseEvent<HTMLElement>) => {
        if (stopPropagation) event.stopPropagation()

        triggerPulse(pulseKey)
    }

    const handlePulseTouchStart = (pulseKey: TApplicationPulseKey, stopPropagation = false) => (event: ReactTouchEvent<HTMLElement>) => {
        if (stopPropagation) event.stopPropagation()

        triggerPulse(pulseKey)
    }

    const handlePulseKeyDown = (pulseKey: TApplicationPulseKey, stopPropagation = false) => (event: ReactKeyboardEvent<HTMLElement>) => {
        if (event.key !== 'Enter' && event.key !== ' ') return

        event.preventDefault()

        if (stopPropagation) event.stopPropagation()

        triggerPulse(pulseKey)
    }

    const getPulseInteractionProps = (pulseKey: TApplicationPulseKey, stopPropagation = false) => ({
        role: 'button' as const,
        tabIndex: 0,
        onMouseDown: handlePulseMouseDown(pulseKey, stopPropagation),
        onTouchStart: handlePulseTouchStart(pulseKey, stopPropagation),
        onKeyDown: handlePulseKeyDown(pulseKey, stopPropagation),
    })

    const currentYearNumber = Number(yearDetailStatsApp?.value || txtCurrentYear?.value || new Date().getFullYear())
    const currentMonthNumber = Number(monthDetailStatsApp?.value || new Date().getMonth() + 1)

    const getWeekdayKeyByDate = (item: Pick<TDayChartItem, 'year' | 'month' | 'day'>): TWeekdayKey => {
        const jsDay = new Date(item.year, item.month - 1, item.day).getDay()

        switch (jsDay) {
            case 1: return 'mon'
            case 2: return 'tue'
            case 3: return 'wed'
            case 4: return 'thu'
            case 5: return 'fri'
            case 6: return 'sat'
            default: return 'sun'
        }
    }

    const getWeekdayShortByDate = (item: Pick<TDayChartItem, 'year' | 'month' | 'day'>) => {
        const weekdayKey = getWeekdayKeyByDate(item)

        switch (weekdayKey) {
            case 'mon': return 'ПН'
            case 'tue': return 'ВТ'
            case 'wed': return 'СР'
            case 'thu': return 'ЧТ'
            case 'fri': return 'ПТ'
            case 'sat': return 'СБ'
            default: return 'ВС'
        }
    }

    const isPreviousMonthTailItem = (item: Partial<TDayChartItem>) => {
        return typeStats.value === 'day'
            && typeof item.day === 'number'
            && typeof item.month === 'number'
            && typeof item.year === 'number'
            && (item.year !== currentYearNumber || item.month !== currentMonthNumber)
    }

    const getMonthShortName = (monthNumber: number) => {
        return getMonthName(monthNumber).slice(0, 3)
    }

    const isAllWeekdaysEnabled = Object.values(weekdays).every(Boolean)

    const filteredItemsDays = useMemo(() => {
        if (isAllWeekdaysEnabled) return itemsDays

        const selectedWeekdays = Object.entries(weekdays)
            .filter(([, value]) => value === false)
            .map(([key]) => key as TWeekdayKey)

        return itemsDays.filter((item) => selectedWeekdays.includes(getWeekdayKeyByDate(item)))
    }, [itemsDays, weekdays, isAllWeekdaysEnabled, currentYearNumber, currentMonthNumber]) // eslint-disable-line react-hooks/exhaustive-deps

    const currentMonthFilteredItemsDays = filteredItemsDays.filter((item) => !isPreviousMonthTailItem(item))
    const displayedDayCount = filteredItemsDays.length
    const currentMonthDisplayedDayCount = currentMonthFilteredItemsDays.length || displayedDayCount
    const currentMonthDayTotals = currentMonthFilteredItemsDays.length === filteredItemsDays.length
        ? totalsDays
        : currentMonthFilteredItemsDays.reduce<CommonStatsAppType>((acc, curr) => ({
            new: acc.new + curr.new,
            delayCall: acc.delayCall + curr.delayCall,
            contracts: acc.contracts + curr.contracts,
            cancel: acc.cancel + curr.cancel,
            notClient: acc.notClient + curr.notClient,
            double: acc.double + curr.double,
            ignore: acc.ignore + curr.ignore,
            total: acc.total + curr.total,
        }), { new: 0, delayCall: 0, contracts: 0, cancel: 0, notClient: 0, double: 0, ignore: 0, total: 0 })

    const handleToggleWeekday = (key: TWeekdayKey) => {
        setWeekdays((prev) => {
            const next = { ...prev, [key]: !prev[key] }

            if (Object.values(next).every((value) => value === false)) {
                return { ...defaultWeekdays }
            }

            return next
        })
    }

    const dayScaleValues = filteredItemsDays.reduce<number[]>((values, item) => [
        ...values,
        ...getChartScaleValues(item)
    ], [])

    const monthScaleValues = itemsMonth.reduce<number[]>((values, item) => [
        ...values,
        ...getChartScaleValues(item)
    ], [])

    const yearScaleValues = itemsYear.reduce<number[]>((values, item) => [
        ...values,
        ...getChartScaleValues(item)
    ], [])

    const minDayValue = getMinPositiveValue(dayScaleValues)
    const minMonthValue = getMinPositiveValue(monthScaleValues)
    const minYearValue = getMinPositiveValue(yearScaleValues)
    const isDayChartCompact = hasCompactChartBars(filteredItemsDays)
    const isMonthChartCompact = hasCompactChartBars(itemsMonth)
    const isYearChartCompact = hasCompactChartBars(itemsYear)
    const dayUnitHeight = isDayChartCompact ? CHART_COMPACT_VALUE_HEIGHT : CHART_MIN_VALUE_HEIGHT
    const monthBaseUnitHeight = isMonthChartCompact ? CHART_COMPACT_VALUE_HEIGHT : CHART_MIN_VALUE_HEIGHT
    const yearBaseUnitHeight = isYearChartCompact ? CHART_COMPACT_VALUE_HEIGHT : CHART_MIN_VALUE_HEIGHT
    const monthUnitHeight = getScaleUnitHeight(minMonthValue, getMaxPositiveValue(monthScaleValues), CHART_AGGREGATED_MAX_HEIGHT, monthBaseUnitHeight)
    const yearUnitHeight = getScaleUnitHeight(minYearValue, getMaxPositiveValue(yearScaleValues), CHART_AGGREGATED_MAX_HEIGHT, yearBaseUnitHeight)
    const dayChartContentMinHeight = getAdaptiveChartContentMinHeight(filteredItemsDays.map((item) => {
        return getStackedTotalHeight(
            getDisplayTotal(item.total),
            minDayValue,
            dayUnitHeight
        )
    }))
    const monthChartContentMinHeight = getAdaptiveChartContentMinHeight(itemsMonth.map((item) => {
        const totalValue = getDisplayTotal(item.total)
        const delayCallValue = getDisplayDelayCall(item.delayCall, item.total)
        const contractsValue = getDisplayContracts(item.contracts, item.total)
        const otherStatusesValue = getDisplayOtherStatuses(getOtherStatusesValue(item), item.total)

        return getStackedTotalHeight(
            totalValue,
            minMonthValue,
            monthUnitHeight,
            [contractsValue, delayCallValue, otherStatusesValue],
            CHART_AGGREGATED_MIN_VISIBLE_HEIGHT
        )
    }))
    const yearChartContentMinHeight = getAdaptiveChartContentMinHeight(itemsYear.map((item) => {
        const totalValue = getDisplayTotal(item.total)
        const delayCallValue = getDisplayDelayCall(item.delayCall, item.total)
        const contractsValue = getDisplayContracts(item.contracts, item.total)
        const otherStatusesValue = getDisplayOtherStatuses(getOtherStatusesValue(item), item.total)

        return getStackedTotalHeight(
            totalValue,
            minYearValue,
            yearUnitHeight,
            [contractsValue, delayCallValue, otherStatusesValue],
            CHART_AGGREGATED_MIN_VISIBLE_HEIGHT
        )
    }))

    const monthYearGroups = itemsMonth.reduce<Array<{ year: number; count: number }>>((acc, item) => {
        const last = acc[acc.length - 1]

        if (last && last.year === item.year) {
            last.count += 1
        } else {
            acc.push({ year: item.year, count: 1 })
        }

        return acc
    }, [])

    const currentGraphItems = typeStats.value === 'day'
        ? currentMonthFilteredItemsDays
        : typeStats.value === 'month'
        ? itemsMonth
        : itemsYear

    const currentGraphTotal = currentGraphItems.reduce((sum, item) => sum + item.total, 0)
    const currentGraphNew = currentGraphItems.reduce((sum, item) => sum + item.new, 0)
    const currentGraphDelayCall = currentGraphItems.reduce((sum, item) => sum + item.delayCall, 0)
    const currentGraphContracts = currentGraphItems.reduce((sum, item) => sum + item.contracts, 0)
    const currentGraphCancel = currentGraphItems.reduce((sum, item) => sum + item.cancel, 0)
    const currentGraphNotClient = currentGraphItems.reduce((sum, item) => sum + item.notClient, 0)
    const currentGraphDouble = currentGraphItems.reduce((sum, item) => sum + item.double, 0)
    const currentGraphIgnore = currentGraphItems.reduce((sum, item) => sum + item.ignore, 0)
    const currentGraphOtherStatuses = currentGraphItems.reduce((sum, item) => sum + getOtherStatusesValue(item), 0)

    const totalStatStyle = {
        background: 'linear-gradient(180deg, rgba(58, 63, 76, .96) 0%, rgba(18, 22, 31, .96) 100%)'
    }

    const applicationStatusRows = [
        {
            key: 'new',
            label: 'Новые',
            total: currentGraphNew,
            valueSelector: (item: TApplicationStatsItem) => item.new,
            style: { background: 'linear-gradient(180deg, rgba(61, 186, 93, .95) 0%, rgba(30, 129, 62, .95) 100%)' }
        },
        {
            key: 'delayCall',
            label: 'Отл. звонок',
            total: currentGraphDelayCall,
            valueSelector: (item: TApplicationStatsItem) => item.delayCall,
            style: { background: 'linear-gradient(180deg, rgba(255, 196, 76, .95) 0%, rgba(218, 138, 0, .95) 100%)' }
        },
        {
            key: 'contracts',
            label: 'Договоры',
            total: currentGraphContracts,
            valueSelector: (item: TApplicationStatsItem) => item.contracts,
            style: { background: 'linear-gradient(180deg, rgba(91, 140, 255, .95) 0%, rgba(19, 73, 221, .95) 100%)' }
        },
        {
            key: 'cancel',
            label: 'Отказ',
            total: currentGraphCancel,
            valueSelector: (item: TApplicationStatsItem) => item.cancel,
            style: { background: 'linear-gradient(180deg, rgba(255, 101, 101, .95) 0%, rgba(224, 35, 35, .95) 100%)' }
        },
        {
            key: 'notClient',
            label: 'Не клиент',
            total: currentGraphNotClient,
            valueSelector: (item: TApplicationStatsItem) => item.notClient,
            style: { background: 'linear-gradient(180deg, rgba(150, 159, 174, .95) 0%, rgba(91, 100, 116, .95) 100%)' }
        },
        {
            key: 'double',
            label: 'Дубль',
            total: currentGraphDouble,
            valueSelector: (item: TApplicationStatsItem) => item.double,
            style: { background: 'linear-gradient(180deg, rgba(76, 83, 97, .95) 0%, rgba(22, 27, 37, .95) 100%)' }
        },
        {
            key: 'ignore',
            label: 'Игнор',
            total: currentGraphIgnore,
            valueSelector: (item: TApplicationStatsItem) => item.ignore,
            style: { background: 'linear-gradient(180deg, rgba(161, 112, 70, .95) 0%, rgba(94, 54, 24, .95) 100%)' }
        }
    ]

    const renderApplicationStatsHeaderRows = () => (
        <>
            {applicationStatusRows.map((row, index) => (
                <p
                    key={`applications-chart-header-${row.key}`}
                    className={`${css['chart_stat_label']} ${index === 0 ? css['chart_stat_label_first'] : ''}`}
                    style={row.style}
                >
                    {row.label} ({row.total}):
                </p>
            ))}

            <p className={css['chart_stat_label']} style={totalStatStyle}>Всего ({currentGraphTotal}):</p>
        </>
    )

    const renderApplicationStatsRows = (items: TChartStatRowItem[]) => (
        <div className={css['chart_stats_rows']}>
            {applicationStatusRows.map((row, rowIndex) => (
                <div
                    key={`applications-chart-row-${row.key}`}
                    className={`${css['chart_stats_row']} ${rowIndex === 0 ? css['chart_stats_row_first'] : ''}`}
                >
                    {items.map((item, index) => (
                        <div
                            key={`applications-chart-cell-${row.key}-${index}`}
                            className={`${css['item']} ${css['month_cell']} ${css['chart_stat_value']} ${isPreviousMonthTailItem(item) ? css['chart_stat_value_previous'] : ''}`}
                            style={row.style}
                        >
                            {row.valueSelector(item)}
                        </div>
                    ))}
                </div>
            ))}

            <div className={css['chart_stats_row']}>
                {items.map((item, index) => (
                    <div
                        key={`applications-chart-total-${index}`}
                        className={`${css['item']} ${css['month_cell']} ${css['chart_stat_value']} ${isPreviousMonthTailItem(item) ? css['chart_stat_value_previous'] : ''}`}
                        style={totalStatStyle}
                    >
                        {item.total}
                    </div>
                ))}
            </div>
        </div>
    )

    const renderDaysRow = (items: TDayChartItem[]) => (
        <div className={css['chart_days_row']}>
            {items.map((item, index) => (
                <div key={`day-${index}`} className={`${css['year']} ${css['month_cell']} ${css['item']} ${css['day_label_wrap']} ${isPreviousMonthTailItem(item) ? css['day_label_previous'] : ''}`}>
                    <span>{item.day}</span>
                    <span className={css['day_week_label']}>{getWeekdayShortByDate(item)}</span>
                    {isPreviousMonthTailItem(item) && <span className={css['day_month_badge']}>{getMonthShortName(item.month)}</span>}
                </div>
            ))}
        </div>
    )

    const contentSettingsStatsModskill = (
        <Flex gap='10px' flexDirection='column' className={css['font_style_content']}>
            <Flex gap="6px" flexDirection='column'>
                <h5 style={{ fontWeight: '800' }}>Параметры статистики</h5>

                <Grid gap='8px' gridTemplate='columns' gridTemplateColumns='1fr 1fr 1fr'>
                    <Button
                        padding='0 10px'
                        size='sm'
                        flexGrow={1}
                        onClick={() => typeStats.setValue('day')}
                        variant={`${typeStats.value === 'day' ? 'primary' : 'primaryDark'}`}
                    >
                        День
                    </Button>

                    <Button
                        padding='0 10px'
                        size='sm'
                        flexGrow={1}
                        onClick={() => typeStats.setValue('month')}
                        variant={`${typeStats.value === 'month' ? 'primary' : 'primaryDark'}`}
                    >
                        Месяц
                    </Button>

                    <Button
                        padding='0 10px'
                        size='sm'
                        flexGrow={1}
                        onClick={() => typeStats.setValue('year')}
                        variant={`${typeStats.value === 'year' ? 'primary' : 'primaryDark'}`}
                    >
                        Год
                    </Button>
                </Grid>
            </Flex>

            <Divider variant='dark' />

            <Flex gap="6px" flexDirection='column'>
                <h5 style={{ fontWeight: '800' }}>Режим графика</h5>

                <Grid gap='8px' gridTemplate='columns' gridTemplateColumns='1fr 1fr'>
                    <Button
                        padding='0 10px'
                        size='sm'
                        flexGrow={1}
                        onClick={() => setChartViewMode('classic')}
                        variant={`${chartViewMode === 'classic' ? 'primary' : 'primaryDark'}`}
                    >
                        Классический
                    </Button>

                    <Button
                        padding='0 10px'
                        size='sm'
                        flexGrow={1}
                        onClick={() => setChartViewMode('percent')}
                        variant={`${chartViewMode === 'percent' ? 'primary' : 'primaryDark'}`}
                    >
                        В процентах
                    </Button>
                </Grid>
            </Flex>

            <Divider variant='dark' />

            <Grid gridTemplate="columns" gridTemplateColumns="1fr auto" alignItems="center" className={css['panel_checkbox']}>
                <p>Статистические данные</p>
                <Toogle checked={showStatisticsData.value} onChange={() => showStatisticsData.setValue(!showStatisticsData.value)} />
            </Grid>

            <Divider variant='dark' />

            <Flex gap="2px" flexDirection='column'>
                <h5 style={{ fontWeight: '800' }}>Дни недели</h5>

                <Grid gridTemplate="columns" gridTemplateColumns="1fr auto" alignItems="center" className={css['panel_checkbox']}>
                    <p>Понедельник</p>
                    <Toogle checked={weekdays.mon} onChange={() => handleToggleWeekday('mon')} />
                </Grid>

                <Grid gridTemplate="columns" gridTemplateColumns="1fr auto" alignItems="center" className={css['panel_checkbox']}>
                    <p>Вторник</p>
                    <Toogle checked={weekdays.tue} onChange={() => handleToggleWeekday('tue')} />
                </Grid>

                <Grid gridTemplate="columns" gridTemplateColumns="1fr auto" alignItems="center" className={css['panel_checkbox']}>
                    <p>Среда</p>
                    <Toogle checked={weekdays.wed} onChange={() => handleToggleWeekday('wed')} />
                </Grid>

                <Grid gridTemplate="columns" gridTemplateColumns="1fr auto" alignItems="center" className={css['panel_checkbox']}>
                    <p>Четверг</p>
                    <Toogle checked={weekdays.thu} onChange={() => handleToggleWeekday('thu')} />
                </Grid>

                <Grid gridTemplate="columns" gridTemplateColumns="1fr auto" alignItems="center" className={css['panel_checkbox']}>
                    <p>Пятница</p>
                    <Toogle checked={weekdays.fri} onChange={() => handleToggleWeekday('fri')} />
                </Grid>

                <Grid gridTemplate="columns" gridTemplateColumns="1fr auto" alignItems="center" className={css['panel_checkbox']}>
                    <p>Суббота</p>
                    <Toogle checked={weekdays.sat} onChange={() => handleToggleWeekday('sat')} />
                </Grid>

                <Grid gridTemplate="columns" gridTemplateColumns="1fr auto" alignItems="center" className={css['panel_checkbox']}>
                    <p>Воскресенье</p>
                    <Toogle checked={weekdays.sun} onChange={() => handleToggleWeekday('sun')} />
                </Grid>
            </Flex>
        </Flex>
    )

    const contentYearsStatsModskill = (
        <Flex gap='4px' flexDirection='column'>
            {itemsYear.length === 0
                ? <Empty image={imgEmty} size='100px' />
                : itemsYear.map((item, index) => {
                    return (
                        <Button
                            key={index}
                            padding='0 10px'
                            size='sm'
                            flexGrow={1}
                            onClick={() => yearDetailStatsApp.setValue(`${item.year}`)}
                            variant={`${yearDetailStatsApp.value === `${item.year}` ? 'primary' : 'primaryDark'}`}
                        >
                            {item.year}
                        </Button>
                    )
                })
            }
        </Flex>
    )

    const contentMonthsStatsModskill = (
        <Flex gap='4px' flexDirection='column'>
            {itemsMonth.filter((item) => item.year === Number(yearDetailStatsApp.value)).length === 0
                ? <Empty image={imgEmty} size='100px' />
                : itemsMonth
                    .filter((item) => item.year === Number(yearDetailStatsApp.value))
                    .map((item, index) => {
                        return (
                            <Button
                                key={index}
                                padding='0 10px'
                                size='sm'
                                flexGrow={1}
                                onClick={() => monthDetailStatsApp.setValue(`${item.month}`)}
                                variant={`${monthDetailStatsApp.value === `${item.month}` ? 'primary' : 'primaryDark'}`}
                            >
                                {getMonthName(item.month)}
                            </Button>
                        )
                    })
            }
        </Flex>
    )

    const getBlocksStats = () => {
        switch (typeStats.value) {
            case 'day': {
                return <>
                    <Container className={css['block_stat_item']} background="rgba(0, 0, 0, .2)">
                        <p style={{ fontWeight: '600', fontSize: '13px' }}>ВСЕГО</p>
                        <p style={{ fontWeight: '800' }}>{currentMonthDayTotals?.total ?? 0}</p>
                    </Container>

                    <Container className={css['block_stat_item']} background="rgba(255, 255, 0, .6)">
                        <p style={{ fontWeight: '600', fontSize: '13px' }}>СРЕДНЕЕ/ДЕНЬ</p>
                        <p style={{ fontWeight: '800' }}>{getAvg(currentMonthDayTotals?.total, currentMonthDisplayedDayCount)}</p>
                    </Container>

                    <Container className={css['block_stat_item']} background="rgba(0, 0, 255, .2)">
                        <p style={{ fontWeight: '600', fontSize: '13px' }}>КОНВЕРСИЯ</p>
                        <p style={{ fontWeight: '800' }}>{getConversionPercent(currentMonthDayTotals?.contracts, currentMonthDayTotals?.total)}%</p>
                    </Container>
                </>
            }

            case 'month': {
                return <>
                    <Container className={css['block_stat_item']} background="rgba(0, 0, 0, .2)">
                        <p style={{ fontWeight: '600', fontSize: '13px' }}>ВСЕГО</p>
                        <p style={{ fontWeight: '800' }}>{totalsYears?.total ?? 0}</p>
                    </Container>

                    <Container className={css['block_stat_item']} background="rgba(255, 255, 0, .6)">
                        <p style={{ fontWeight: '600', fontSize: '13px' }}>СРЕДНЕЕ/МЕСЯЦ</p>
                        <p style={{ fontWeight: '800' }}>{getAvg(totalsYears?.total, itemsMonth.length)}</p>
                    </Container>

                    <Container className={css['block_stat_item']} background="rgba(0, 0, 255, .2)">
                        <p style={{ fontWeight: '600', fontSize: '13px' }}>КОНВЕРСИЯ</p>
                        <p style={{ fontWeight: '800' }}>{getConversionPercent(totalsYears?.contracts, totalsYears?.total)}%</p>
                    </Container>
                </>
            }

            case 'year': {
                return <>
                    <Container className={css['block_stat_item']} background="rgba(0, 0, 0, .2)">
                        <p style={{ fontWeight: '600', fontSize: '13px' }}>ЗАЯВКИ</p>
                        <p style={{ fontWeight: '800' }}>{totalsYears?.total ?? 0}</p>
                    </Container>

                    <Container className={css['block_stat_item']} background="rgba(255, 255, 0, .6)">
                        <p style={{ fontWeight: '600', fontSize: '13px' }}>СРЕДНЕЕ/ГОД</p>
                        <p style={{ fontWeight: '800' }}>{getAvg(totalsYears?.total, itemsYear.length)}</p>
                    </Container>

                    <Container className={css['block_stat_item']} background="rgba(0, 0, 255, .2)">
                        <p style={{ fontWeight: '600', fontSize: '13px' }}>КОНВЕРСИЯ</p>
                        <p style={{ fontWeight: '800' }}>{getConversionPercent(totalsYears?.contracts, totalsYears?.total)}%</p>
                    </Container>
                </>
            }

            default:
                return null
        }
    }

    const renderLegendTotal = () => (
        <p className={getPulseTargetClassName('total', css['legend_item'], css['legend_total'])} {...getPulseInteractionProps('total')}>
            {chartViewMode === 'percent' ? `Заявки, % (${currentGraphTotal})` : `Заявки (${currentGraphTotal})`}
        </p>
    )

    const renderLegendDelayCall = () => (
        <p className={getPulseTargetClassName('delayCall', css['legend_item'], css['legend_delay_call'])} {...getPulseInteractionProps('delayCall')}>
            {chartViewMode === 'percent' ? `Отл. звонок, % (${currentGraphDelayCall})` : `Отл. звонок (${currentGraphDelayCall})`}
        </p>
    )

    const renderLegendContracts = () => (
        <p className={getPulseTargetClassName('contracts', css['legend_item'], css['legend_contracts'])} {...getPulseInteractionProps('contracts')}>
            {chartViewMode === 'percent' ? `Договоры, % (${currentGraphContracts})` : `Договоры (${currentGraphContracts})`}
        </p>
    )

    const renderLegendOtherStatuses = () => (
        <p className={getPulseTargetClassName('otherStatuses', css['legend_item'], css['legend_other_statuses'])} {...getPulseInteractionProps('otherStatuses')}>
            {chartViewMode === 'percent' ? `Прочее, % (${currentGraphOtherStatuses})` : `Прочее (${currentGraphOtherStatuses})`}
        </p>
    )

    const renderStackedInnerBars = (
        minValue: number,
        unitHeight: number,
        totalLabel: string | number,
        contractsValue: number,
        delayCallValue: number,
        otherStatusesValue: number,
        contractsLabel: string | number,
        delayCallLabel: string | number,
        otherStatusesLabel: string | number,
        minVisibleHeight = 0
    ) => {
        const [contractsHeight, delayCallHeight, otherStatusesHeight] = getStackedSegmentHeights(
            [contractsValue, delayCallValue, otherStatusesValue],
            minValue,
            unitHeight,
            minVisibleHeight
        )

        const delayCallBottom = contractsHeight
        const otherStatusesBottom = contractsHeight + delayCallHeight

        return <>
            <p
                className={getPulseTargetClassName('total', css['item_header'], css['item_header_total'])}
                {...getPulseInteractionProps('total', true)}
            >
                {totalLabel}
            </p>

            {contractsHeight > 0 &&
                <div
                    className={getPulseTargetClassName('contracts', css['item'], css['item_inner_bar'], css['item_inner_bar_contracts'], css['contracts'])}
                    style={{ height: `${contractsHeight}px`, bottom: 0 }}
                    {...getPulseInteractionProps('contracts', true)}
                >
                    <p className={css['item_header_inner']}>
                        {contractsLabel}
                    </p>
                </div>
            }

            {delayCallHeight > 0 &&
                <div
                    className={getPulseTargetClassName('delayCall', css['item'], css['item_inner_bar'], css['item_inner_bar_delay_call'], css['delay_call'])}
                    style={{ height: `${delayCallHeight}px`, bottom: `${delayCallBottom}px` }}
                    {...getPulseInteractionProps('delayCall', true)}
                >
                    <p className={css['item_header_inner']}>
                        {delayCallLabel}
                    </p>
                </div>
            }

            {otherStatusesHeight > 0 &&
                <div
                    className={getPulseTargetClassName('otherStatuses', css['item'], css['item_inner_bar'], css['item_inner_bar_other_statuses'], css['other_statuses'])}
                    style={{ height: `${otherStatusesHeight}px`, bottom: `${otherStatusesBottom}px` }}
                    {...getPulseInteractionProps('otherStatuses', true)}
                >
                    <p className={css['item_header_inner']}>
                        {otherStatusesLabel}
                    </p>
                </div>
            }
        </>
    }

    return <>
        <Container className={css['container']}>
            <div className={css['container_header']}>
                <Grid gap="10px" gridTemplate="columns" gridTemplateColumns="1fr auto">
                    <Flex gap='6px' flexDirection='row' alignItems='center'>
                        <IconApplications width="26px" height="26px" />
                        <h4>Заявки YaSkill</h4>
                    </Flex>

                    <Flex gap='10px' alignItems="center" flexDirection='row'>
                        <Flex gap='10px' flexDirection='row' isRendered={typeStats.value === 'day'}>
                            <Popover
                                content={contentMonthsStatsModskill}
                                title="Выберите месяц"
                                trigger="click"
                                placement="bottomRight"
                                styles={{ root: { width: '180px' } }}
                            >
                                <div>
                                    <Button size='sm' variant='icon' padding='0 10px'>
                                        {txtCurrentMonth.value}
                                    </Button>
                                </div>
                            </Popover>

                            <Popover
                                content={contentYearsStatsModskill}
                                title="Выберите год"
                                trigger="click"
                                placement="bottomRight"
                                styles={{ root: { width: '150px' } }}
                            >
                                <div>
                                    <Button size='sm' variant='icon' padding='0 10px'>
                                        {txtCurrentYear.value}
                                    </Button>
                                </div>
                            </Popover>

                            <Divider type="vertical" size="20px" variant={'dark'} />
                        </Flex>

                        <Popover
                            content={contentSettingsStatsModskill}
                            trigger="click"
                            placement="bottomRight"
                            styles={{ root: { width: '320px' } }}
                        >
                            <div>
                                <Button size='sm' variant='icon'>
                                    <IconSettings width='20px' height='20px' />
                                </Button>
                            </div>
                        </Popover>
                    </Flex>
                </Grid>

                <Divider variant='dark' />

                <Flex flexDirection="row" className={css['block_stat_conteiner']}>
                    {getBlocksStats()}
                </Flex>
            </div>

            {typeStats.value === 'day' &&
                <div className={css['chart_container']}>
                    <div className={css['header']}>
                        {renderLegendTotal()}
                        {renderLegendDelayCall()}
                        {renderLegendContracts()}
                        {renderLegendOtherStatuses()}
                        <div style={{ flexGrow: 1 }}></div>
                        <p className={css['year']}>День:</p>
                        {showStatisticsData.value && renderApplicationStatsHeaderRows()}
                    </div>

                    <div className={css['chart_scroll']} {...dragScrollProps}>
                        <div className={css['chart_inner']}>
                            <div className={`${css['chart_content']} ${css['chart_content_external_labels']} ${isDayChartCompact ? css['chart_content_compact_bars'] : ''}`} style={{ minHeight: `${dayChartContentMinHeight}px` }}>
                                {filteredItemsDays.length === 0
                                    ? (
                                        <div className={css['chart_loader']}>
                                            <Loader isOpen={true} backdrop />
                                        </div>
                                    )
                                    : filteredItemsDays.map((item, index) => {
                                        const isPreviousMonthTail = isPreviousMonthTailItem(item)
                                        const totalValue = getDisplayTotal(item.total)
                                        const delayCallValue = getDisplayDelayCall(item.delayCall, item.total)
                                        const contractsValue = getDisplayContracts(item.contracts, item.total)
                                        const otherStatusesValue = getDisplayOtherStatuses(getOtherStatusesValue(item), item.total)
                                        const totalHeight = getStackedTotalHeight(
                                            totalValue,
                                            minDayValue,
                                            dayUnitHeight
                                        )

                                        return (
                                            <div key={index} className={`${css['chart_col']} ${isPreviousMonthTail ? css['chart_col_previous'] : ''}`}>
                                                <div className={css['inset']}>
                                                    <div
                                                        hidden={item.total === 0}
                                                        className={`${css['item']} ${css['item_total']}`}
                                                        style={{ height: `${totalHeight}px` }}
                                                    >
                                                        {item.total > 0 && renderStackedInnerBars(
                                                            minDayValue,
                                                            dayUnitHeight,
                                                            chartViewMode === 'percent' ? '100%' : item.total,
                                                            contractsValue,
                                                            delayCallValue,
                                                            otherStatusesValue,
                                                            chartViewMode === 'percent'
                                                                ? `${getPercentValue(item.contracts, item.total)}%`
                                                                : item.contracts,
                                                            chartViewMode === 'percent'
                                                                ? `${getPercentValue(item.delayCall, item.total)}%`
                                                                : item.delayCall,
                                                            chartViewMode === 'percent'
                                                                ? `${getPercentValue(getOtherStatusesValue(item), item.total)}%`
                                                                : getOtherStatusesValue(item)
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        )
                                    })
                                }
                            </div>

                            {filteredItemsDays.length > 0 && renderDaysRow(filteredItemsDays)}

                            {showStatisticsData.value && filteredItemsDays.length > 0 && renderApplicationStatsRows(filteredItemsDays)}
                        </div>
                    </div>
                </div>
            }

            {typeStats.value === 'month' &&
                <div className={css['chart_container']}>
                    <div className={css['header']}>
                        {renderLegendTotal()}
                        {renderLegendDelayCall()}
                        {renderLegendContracts()}
                        {renderLegendOtherStatuses()}
                        <div style={{ flexGrow: 1 }}></div>
                        <p className={css['year']}>Месяц:</p>
                        <p className={css['year']}>Год:</p>
                        {showStatisticsData.value && renderApplicationStatsHeaderRows()}
                    </div>

                    <div className={css['chart_scroll']} {...dragScrollProps}>
                        <div className={css['chart_inner']}>
                            <div className={`${css['chart_content']} ${css['chart_content_external_labels']} ${isMonthChartCompact ? css['chart_content_compact_bars'] : ''}`} style={{ minHeight: `${monthChartContentMinHeight}px` }}>
                                {itemsMonth.length === 0
                                    ? (
                                        <div className={css['chart_loader']}>
                                            <Loader isOpen={true} backdrop />
                                        </div>
                                    )
                                    : itemsMonth.map((item, index) => {
                                        const totalValue = getDisplayTotal(item.total)
                                        const delayCallValue = getDisplayDelayCall(item.delayCall, item.total)
                                        const contractsValue = getDisplayContracts(item.contracts, item.total)
                                        const otherStatusesValue = getDisplayOtherStatuses(getOtherStatusesValue(item), item.total)
                                        const totalHeight = getStackedTotalHeight(
                                            totalValue,
                                            minMonthValue,
                                            monthUnitHeight,
                                            [contractsValue, delayCallValue, otherStatusesValue],
                                            CHART_AGGREGATED_MIN_VISIBLE_HEIGHT
                                        )

                                        return (
                                            <div key={index} className={css['chart_col']}>
                                                <div className={css['inset']}>
                                                    <div
                                                        hidden={item.total === 0}
                                                        className={`${css['item']} ${css['item_total']}`}
                                                        style={{ height: `${totalHeight}px` }}
                                                    >
                                                        {item.total > 0 && renderStackedInnerBars(
                                                            minMonthValue,
                                                            monthUnitHeight,
                                                            chartViewMode === 'percent' ? '100%' : item.total,
                                                            contractsValue,
                                                            delayCallValue,
                                                            otherStatusesValue,
                                                            chartViewMode === 'percent'
                                                                ? `${getPercentValue(item.contracts, item.total)}%`
                                                                : item.contracts,
                                                            chartViewMode === 'percent'
                                                                ? `${getPercentValue(item.delayCall, item.total)}%`
                                                                : item.delayCall,
                                                            chartViewMode === 'percent'
                                                                ? `${getPercentValue(getOtherStatusesValue(item), item.total)}%`
                                                                : getOtherStatusesValue(item),
                                                            CHART_AGGREGATED_MIN_VISIBLE_HEIGHT
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        )
                                    })
                                }
                            </div>

                            <div className={css['chart_months_row']}>
                                {itemsMonth.map((item, index) => (
                                    <div
                                        key={`month-${index}`}
                                        className={`${css['year']} ${css['month_label']} ${css['month_cell']} ${css['item']}`}
                                    >
                                        {item.month}
                                    </div>
                                ))}
                            </div>

                            <div className={css['chart_years_row']}>
                                {monthYearGroups.map((group, index) => (
                                    <div
                                        key={`${group.year}-${index}`}
                                        className={css['chart_year_group']}
                                        style={{ width: `calc(${group.count} * 32px + ${(group.count - 1)} * 5px)` }}
                                    >
                                        {group.year}
                                    </div>
                                ))}
                            </div>
                            {showStatisticsData.value && itemsMonth.length > 0 && renderApplicationStatsRows(itemsMonth)}
                        </div>
                    </div>
                </div>
            }

            {typeStats.value === 'year' &&
                <div className={css['chart_container']}>
                    <div className={css['header']}>
                        {renderLegendTotal()}
                        {renderLegendDelayCall()}
                        {renderLegendContracts()}
                        {renderLegendOtherStatuses()}
                        <div style={{ flexGrow: 1 }}></div>
                        <p className={css['year']}>Год:</p>
                        {showStatisticsData.value && renderApplicationStatsHeaderRows()}
                    </div>

                    <div className={css['chart_scroll']} {...dragScrollProps}>
                        <div className={css['chart_inner']}>
                            <div className={`${css['chart_content']} ${isYearChartCompact ? css['chart_content_compact_bars'] : ''}`} style={{ minHeight: `${yearChartContentMinHeight}px` }}>
                                {itemsYear.length === 0
                                    ? (
                                        <div className={css['chart_loader']}>
                                            <Loader isOpen={true} backdrop />
                                        </div>
                                    )
                                    : itemsYear.map((item, index) => {
                                        const totalValue = getDisplayTotal(item.total)
                                        const delayCallValue = getDisplayDelayCall(item.delayCall, item.total)
                                        const contractsValue = getDisplayContracts(item.contracts, item.total)
                                        const otherStatusesValue = getDisplayOtherStatuses(getOtherStatusesValue(item), item.total)
                                        const totalHeight = getStackedTotalHeight(
                                            totalValue,
                                            minYearValue,
                                            yearUnitHeight,
                                            [contractsValue, delayCallValue, otherStatusesValue],
                                            CHART_AGGREGATED_MIN_VISIBLE_HEIGHT
                                        )

                                        return (
                                            <div key={index} className={css['chart_col']}>
                                                <div className={css['inset']}>
                                                    <div
                                                        hidden={item.total === 0}
                                                        className={`${css['item']} ${css['item_total']}`}
                                                        style={{ height: `${totalHeight}px` }}
                                                    >
                                                        {item.total > 0 && renderStackedInnerBars(
                                                            minYearValue,
                                                            yearUnitHeight,
                                                            chartViewMode === 'percent' ? '100%' : item.total,
                                                            contractsValue,
                                                            delayCallValue,
                                                            otherStatusesValue,
                                                            chartViewMode === 'percent'
                                                                ? `${getPercentValue(item.contracts, item.total)}%`
                                                                : item.contracts,
                                                            chartViewMode === 'percent'
                                                                ? `${getPercentValue(item.delayCall, item.total)}%`
                                                                : item.delayCall,
                                                            chartViewMode === 'percent'
                                                                ? `${getPercentValue(getOtherStatusesValue(item), item.total)}%`
                                                                : getOtherStatusesValue(item),
                                                            CHART_AGGREGATED_MIN_VISIBLE_HEIGHT
                                                        )}
                                                    </div>
                                                </div>

                                                <div className={`${css['year']} ${css['item']}`}>
                                                    {item.year}
                                                </div>
                                            </div>
                                        )
                                    })
                                }
                            </div>
                            {showStatisticsData.value && itemsYear.length > 0 && renderApplicationStatsRows(itemsYear)}
                        </div>
                    </div>
                </div>
            }
        </Container>
    </>
}